"use client";

import { formatSumInsured } from "@/utils/sumInsured";
import React from "react";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { useErrorDialog } from "@/context/ErrorDialogContext";
import { useFunnelRoot } from "@/shadow/ShadowRootContext";
import { useTranslation } from "react-i18next";
import { ProductVariant } from "@/hooks/useFunnelState";
import {
  BASE_COVERAGES,
  BaseCoverageDef,
  BaseCoverageId,
  SubCoverageDef,
  DEFAULT_SUB_SUM_INSURED,
  baseSumInsuredMax,
  getBaseCoverage,
  isCoverageOn,
  isFullCoverRequested,
  WAIVER_HEREDITARY_LIMITS_KEY,
  primaryLineId,
  sharedLineIds,
  subSumInsuredMax,
  UNLIMITED_SUM_INSURED,
} from "@/config/coverages";
import {
  CoverageState,
  SUM_INSURED_MIN,
  ANNUAL_DEDUCTIBLE_MAX,
  DEDUCTIBLE_PERCENTAGE_MIN,
  DEDUCTIBLE_PERCENTAGE_MAX,
  SUB_SUM_INSURED_MIN,
  isPetYoungerThanAgeBand,
  getEmergencyCareDefaultSumPerDay,
} from "@/utils/coveragePayload";

import { getEarliestStartOfInsurance, getTomorrowAtMidnight } from "@/utils/funnel";
import StartOfInsuranceInput, {
  computeEarliestStartOfInsurance,
  validateStartOfInsuranceDate,
} from "./pet-details/StartOfInsuranceInput";
import DeductibleResetDateInput from "./price-customization/DeductibleResetDateInput";
import PresetValueField from "./price-customization/PresetValueField";
import { CHECK_INDENT, CheckBoxMark, CoverageCheck } from "./price-customization/CoverageCheck";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { NumericInput } from "@/components/ui/numeric-input";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Info, Loader2 } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useAgentId } from "@/context/AgentIdContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";


interface ProductCustomizationOptionsProps {
  petName: string;
  onCustomizeProduct: (
    product: ProductVariant | null,
    finalPrice: number,
    coverageDeductibles: Partial<Record<BaseCoverageId, number>>,
    subCoverageSelections: Partial<Record<BaseCoverageId, Record<string, boolean>>>,
    coverageSumInsured: Partial<Record<BaseCoverageId, number>>,
    dailyTermination: boolean,
    paymentInterval: 1 | 2 | 4 | 12,
    paymentMethod: "credit_card" | "ESR",
    startOfInsurance: Date
  ) => void;
  onBack: () => void;
  selectedProduct: ProductVariant | null;
  hasSelectedPlan: boolean;
  /** Decides, for age-banded coverages (disease, preexistingConditions), which deductible band
   * `coverageDeductibles` represents — see `isPetYoungerThanAgeBand` in coveragePayload.ts. */
  petBirthDate: Date | null;
  coverageSelections: Partial<Record<BaseCoverageId, boolean>>;
  setCoverageSelections: (next: Partial<Record<BaseCoverageId, boolean>>) => void;
  coverageDeductibles: Partial<Record<BaseCoverageId, number>>;
  setCoverageDeductibles: (next: Partial<Record<BaseCoverageId, number>>) => void;
  /** The other age band's percentage — never shown/edited here, just carried through so
   * submitting doesn't silently overwrite it with `coverageDeductibles`' value. */
  coverageDeductiblesOtherBand: Partial<Record<BaseCoverageId, number>>;
  setCoverageDeductiblesOtherBand: (next: Partial<Record<BaseCoverageId, number>>) => void;
  subCoverageSelections: Partial<Record<BaseCoverageId, Record<string, boolean>>>;
  setSubCoverageSelections: (next: Partial<Record<BaseCoverageId, Record<string, boolean>>>) => void;
  subCoverageSumInsured: Partial<Record<BaseCoverageId, Record<string, number>>>;
  setSubCoverageSumInsured: (next: Partial<Record<BaseCoverageId, Record<string, number>>>) => void;
  coverageSumInsured: Partial<Record<BaseCoverageId, number>>;
  setCoverageSumInsured: (next: Partial<Record<BaseCoverageId, number>>) => void;
  /** Disease/accident's `sumInsuredPerCase` — not editable in this UI, just carried through
   * from whatever the backend last recommended so it isn't clobbered on submit. */
  coverageSumInsuredPerCase: Partial<Record<BaseCoverageId, string>>;
  setCoverageSumInsuredPerCase: (next: Partial<Record<BaseCoverageId, string>>) => void;
  /** Decides the Notfallbetreuung default per day (cats only get CHF 50). */
  petType: "cat" | "dog" | null;
  emergencyCareSumInsuredPerDay: "50" | "100" | null;
  setEmergencyCareSumInsuredPerDay: (value: "50" | "100" | null) => void;
  /** Krankheit und Unfall's Jahresfranchise in CHF (shared by both lines). */
  annualDeductible: number | null;
  setAnnualDeductible: (value: number | null) => void;
  dailyTermination: boolean;
  setDailyTermination: (value: boolean) => void;
  paymentInterval: 1 | 2 | 4 | 12;
  setPaymentInterval: (interval: 1 | 2 | 4 | 12) => void;
  paymentMethod: "credit_card" | "ESR";
  setPaymentMethod: (method: "credit_card" | "ESR") => void;
  startOfInsurance: Date;
  setStartOfInsurance: (date: Date) => void;
  firstDue: Date | null;
  setFirstDue: (date: Date | null) => void;
  currentApiPrice: number | null;
  coveragePremiums: Partial<Record<BaseCoverageId, number>>;
  /** Teilprämie (incl. tax) of the tägliches Kündigungsrecht while it is selected, from the live offer. */
  dailyTerminationPremium?: number | null;
  declinedSubs: Partial<Record<BaseCoverageId, Record<string, boolean>>>;
  /** The backend's own `underwritingReason` for a coverage that had one or more subs declined
   * — SubTreatment itself never carries a reason, so this is the closest real explanation
   * available (openapi: only the parent Disease/Accident/PreexistingConditions has it). */
  coverageUnderwritingReasons: Partial<Record<BaseCoverageId, string>>;
  /** A coverage the customer requested that the backend rejected outright (still comes back
   * `requested: true` with a `netPremiumProRata` of 0, which otherwise reads as a legitimately
   * free coverage) — keyed by the coverage's own underwritingReason explaining why. */
  declinedCoverages: Partial<Record<BaseCoverageId, string>>;
  fetchCoverageRecommendation: (
    targetCoverageId: BaseCoverageId,
    currentCoverageState: CoverageState
  ) => Promise<CoverageState | null>;
  isLoadingPrice: boolean;
  underwritingFailed: boolean;
  underwritingReason: string | null;
}

/** A checkbox the customer cannot change (always-included benefits, fixed sub-coverages). */
const LockedCheck: React.FC<{ checked: boolean; label: string; children?: React.ReactNode }> = ({ checked, label, children }) => (
  <div role="checkbox" aria-checked={checked} aria-disabled="true" aria-label={label} className="flex min-w-0 items-center gap-3">
    <CheckBoxMark checked={checked} locked />
    {children}
  </div>
);

/** Opaque version of the band's grey (muted at 30% on the page background), so content scrolling
 * underneath a stuck band does not shine through. */
const BAND_COLOR = "color-mix(in srgb, hsl(var(--muted)) 30%, hsl(var(--background)))";

/** A coverage block: a grey heading band like the plan table's section rows, as wide as the cart box above
 * (it cancels the card's own padding), white body. */
const CoverageSection: React.FC<{ title: React.ReactNode; aside?: React.ReactNode; children?: React.ReactNode }> = ({ title, aside, children }) => (
  <section>
    <div className="sticky top-(--cart-offset) z-10 flex flex-wrap items-start justify-between gap-x-4 gap-y-1 -mx-6 bg-(--band) px-6 py-2.5">
      <div className="min-w-[14rem] flex-1">{title}</div>
      <div className="ml-auto shrink-0">{aside}</div>
    </div>
    {children && <div className="space-y-5 pb-2 pt-4">{children}</div>}
  </section>
);

const InfoDialog: React.FC<{ title: string; srLabel?: string; children: React.ReactNode }> = ({ title, srLabel, children }) => {
  const { t } = useTranslation();
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0">
          <Info className="h-5! w-5! text-muted-foreground" />
          <span className="sr-only">{`${t("common.info")}: ${srLabel ?? title}`}</span>
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="whitespace-pre-line">{children}</DialogDescription>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
};

/** Shown next to a sub-coverage the customer requested but the backend didn't grant on the
 * last calculation — the openapi `SubTreatment` schema has no underwriting/feedback fields of
 * its own, so a mismatch between what we sent and what came back is the only available signal. */
const UwrDeclinedBadge: React.FC<{ label: string; reason?: string }> = ({ label, reason }) => (
  <span
    title={reason}
    className="inline-flex items-center rounded-full text-xs font-semibold px-2 py-0.5 bg-destructive/10 text-destructive shrink-0"
  >
    {label}
  </span>
);

/** Editable `SubTreatment.sumInsured` (openapi: integer, 1–100'000, required whenever the
 * sub is requested). Kept as a small inline field next to the sub's own versichert/nicht
 * versichert toggle. */
const SUB_SUM_FALLBACK = 5000;
const SubSumInput: React.FC<{
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  max: number;
  disabled?: boolean;
  error?: string;
  onZero?: () => void;
  /** Sum to fall back to when "unlimitiert" is un-ticked and no earlier limited value is known. */
  fallback?: number;
}> = ({ value, onChange, max, disabled, error, onZero, fallback }) => {
  const { t } = useTranslation();
  // Only sums that can reach the maximum (100'000, i.e. well above the 25'000 free-entry limit) are "unlimitiert".
  const hasUnlimited = max >= UNLIMITED_SUM_INSURED;
  const isUnlimited = hasUnlimited && value != null && value >= max;
  const lastLimited = React.useRef<number | undefined>(undefined);
  React.useEffect(() => {
    if (value != null && !isUnlimited) lastLimited.current = value;
  }, [value, isUnlimited]);
  // Anything above the maximum (e.g. an "unlimited" figure from a recommendation) is simply the maximum = unlimitiert.
  React.useEffect(() => {
    if (hasUnlimited && value != null && value > max) onChange(max);
  }, [hasUnlimited, value, max, onChange]);

  const handleUnlimitedToggle = (checked: boolean) => {
    onChange(checked ? max : (lastLimited.current ?? fallback ?? SUB_SUM_FALLBACK));
  };

  return (
    <div className="flex basis-full flex-wrap items-center gap-x-3 gap-y-1 sm:basis-auto sm:justify-end">
      {isUnlimited ? (
        <Input value={t("coverages.sumInsuredUnlimited")} disabled readOnly aria-label={t("coverages.unlimited")} className="w-28 text-right" />
      ) : (
        <NumericInput
          value={value}
          onChange={onChange}
          onZero={onZero}
          min={1}
          max={max}
          suffix="CHF"
          disabled={disabled}
          error={!!error}
          className="w-28 text-right"
        />
      )}
      {!error && !isUnlimited && <p className="whitespace-nowrap text-xs text-muted-foreground">{t("coverages.subSumInsuredHint")}</p>}
      {hasUnlimited && (
        <CoverageCheck checked={isUnlimited} label={t("coverages.unlimited")} disabled={disabled} onChange={handleUnlimitedToggle}>
          <span className="text-sm">{t("coverages.unlimited")}</span>
        </CoverageCheck>
      )}
      {error && <p className="basis-full text-xs text-destructive sm:text-right">{error}</p>}
    </div>
  );
};

/** Editable base-coverage-level `sumInsured` of Krankheit und Unfall (openapi: integer): a dropdown of preset sums, with
 * "unlimitiert" (also a checkbox next to it, which pins the sum to the maximum) and "frei wählen" (text field; an entry above
 * `SUM_INSURED_FREE_MAX` becomes unlimited). Disease and accident share one value, so this is rendered once for the merged card. */
const SUM_INSURED_PRESETS = [3000, 6000, 12000, 20000];
const SUM_INSURED_FREE_MIN = 1000;
const SUM_INSURED_FREE_MAX = 25000;

const SumInsuredInput: React.FC<{
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  max: number;
  disabled?: boolean;
  error?: string;
  id?: string;
}> = ({ value, onChange, max, disabled, error, id = "sum-insured" }) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <Label htmlFor={id} className="text-sm font-medium">{t("coverages.sumInsuredLabel")}</Label>
        <InfoDialog title={t("coverages.sumInsuredInfoTitle")}>{t("coverages.sumInsuredInfoDescription")}</InfoDialog>
      </div>
      <PresetValueField
        id={id}
        value={value}
        onChange={onChange}
        presets={SUM_INSURED_PRESETS}
        formatValue={(preset) => `CHF ${preset.toLocaleString("de-CH")}`}
        min={SUM_INSURED_MIN}
        max={max}
        suffix="CHF"
        unlimitedAt={max}
        freeMax={SUM_INSURED_FREE_MAX}
        disabled={disabled}
        error={error}
        requiredMessage={t("coverages.validation.sumInsuredRequired")}
        hint={t("coverages.sumInsuredHint", {
          min: SUM_INSURED_FREE_MIN.toLocaleString("de-CH"),
          max: SUM_INSURED_FREE_MAX.toLocaleString("de-CH"),
        })}
      />
    </div>
  );
};

/** Vorerkrankung's own sum insured (openapi: 1–10'000): a dropdown of preset sums plus "frei wählen". */
const PREEXISTING_SUM_INSURED_PRESETS = [1500, 3000, 6000];
const PREEXISTING_SUM_INSURED_MIN = 1;
const PREEXISTING_SUM_INSURED_MAX = 10000;

const COST_SHARE_PRESETS = [50, 60, 70, 80, 90, 100];
/** The Selbstbehalt the backend stores is 0–80 %, so the Kostenübernahme is 20–100 %. */
const COST_SHARE_MIN = 100 - DEDUCTIBLE_PERCENTAGE_MAX;
const COST_SHARE_MAX = 100 - DEDUCTIBLE_PERCENTAGE_MIN;

/** Kostenübernahme in % — the counterpart of the Selbstbehalt the backend stores (`claim_deductible_percentage`).
 * `value` and `onChange` stay in Selbstbehalt percent; only the display is inverted. */
const CostShareField: React.FC<{
  id: string;
  label: string;
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  onEmptyChange?: (empty: boolean) => void;
  disabled?: boolean;
}> = ({ id, label, value, onChange, onEmptyChange, disabled }) => {
  const { t } = useTranslation();
  return (
    <div className="space-y-1">
      <Label htmlFor={id} className="text-sm font-medium">{label}</Label>
      <PresetValueField
        id={id}
        value={value == null ? undefined : 100 - value}
        onChange={(share) => onChange(share == null ? undefined : 100 - share)}
        presets={COST_SHARE_PRESETS}
        formatValue={(preset) => `${preset}%`}
        min={COST_SHARE_MIN}
        max={COST_SHARE_MAX}
        suffix="%"
        disabled={disabled}
        onEmptyChange={onEmptyChange}
        requiredMessage={t("coverages.costShare.customRequired")}
        hint={t("coverages.costShare.customHint", { min: COST_SHARE_MIN, max: COST_SHARE_MAX })}
      />
      {value != null && <p className="text-xs text-muted-foreground">{t("coverages.costShare.selfPays", { percent: value })}</p>}
    </div>
  );
};

const ANNUAL_DEDUCTIBLE_PRESETS = [0, 200, 500, 1000, 2000];

const ProductCustomizationOptions: React.FC<ProductCustomizationOptionsProps> = ({
  petName,
  onCustomizeProduct,
  onBack,
  selectedProduct,
  hasSelectedPlan,
  petBirthDate,
  coverageSelections,
  setCoverageSelections,
  coverageDeductibles,
  setCoverageDeductibles,
  coverageDeductiblesOtherBand,
  setCoverageDeductiblesOtherBand,
  subCoverageSelections,
  setSubCoverageSelections,
  subCoverageSumInsured,
  setSubCoverageSumInsured,
  coverageSumInsured,
  setCoverageSumInsured,
  coverageSumInsuredPerCase,
  setCoverageSumInsuredPerCase,
  petType,
  emergencyCareSumInsuredPerDay,
  setEmergencyCareSumInsuredPerDay,
  annualDeductible,
  setAnnualDeductible,
  dailyTermination,
  setDailyTermination,
  paymentInterval,
  setPaymentInterval,
  paymentMethod,
  setPaymentMethod,
  startOfInsurance,
  setStartOfInsurance,
  firstDue,
  setFirstDue,
  currentApiPrice,
  coveragePremiums,
  dailyTerminationPremium,
  declinedSubs,
  coverageUnderwritingReasons,
  declinedCoverages,
  fetchCoverageRecommendation,
  isLoadingPrice,
  underwritingFailed,
  underwritingReason,
}) => {
  const { t } = useTranslation();
  const { isCustomAgentId } = useAgentId();
  const { showErrorDialog } = useErrorDialog();
  const [startOfInsuranceError, setStartOfInsuranceError] = React.useState<string | null>(null);

  const earliestStartOfInsurance = React.useMemo(
    () => computeEarliestStartOfInsurance(petBirthDate ?? undefined),
    [petBirthDate]
  );

  // Opened from the pencil next to "Zahlung und Vertragsbeginn" on the summary page: land on the
  // payment section instead of the top of the page.
  const location = useLocation();
  const funnelRoot = useFunnelRoot();

  // Section heading bands stick right below the sticky price box ("Warenkorb"); its height changes
  // when it expands/collapses, so follow it.
  const [cartOffset, setCartOffset] = React.useState(0);
  React.useEffect(() => {
    let observer: ResizeObserver | undefined;
    // After a reload the cart is rendered later than this page (it waits for the selected plan), so look
    // for it until it shows up instead of once on mount.
    const attach = () => {
      const cart = funnelRoot.getElementById("funnel-cart");
      if (!cart) return false;
      // The wrapper carries a bottom margin; the visible box is its first child.
      const box = cart.firstElementChild ?? cart;
      const update = () => setCartOffset(box.getBoundingClientRect().height + (parseFloat(getComputedStyle(cart).top) || 0)); // + the cart's own sticky `top`
      update();
      observer = new ResizeObserver(update);
      observer.observe(box);
      return true;
    };
    const timer = attach() ? undefined : window.setInterval(() => attach() && window.clearInterval(timer), 150);
    return () => {
      window.clearInterval(timer);
      observer?.disconnect();
    };
  }, [funnelRoot]);
  const scrollToPayment = (location.state as { scrollTo?: string } | null)?.scrollTo === "payment";
  React.useEffect(() => {
    if (!scrollToPayment) {
      window.scrollTo(0, 0);
      return;
    }
    // Wait a frame so the coverage cards above have laid out before measuring.
    const frame = requestAnimationFrame(() => {
      funnelRoot.getElementById("payment-options")?.scrollIntoView({ block: "start" });
    });
    return () => cancelAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Effect to enforce payment method for monthly interval
  React.useEffect(() => {
    if (paymentInterval === 12 && paymentMethod === "ESR") {
      setPaymentMethod("credit_card");
    }
  }, [paymentInterval, paymentMethod, setPaymentMethod]);

  // The selection the plan table (and its recommendations) was computed for: "Anpassungen verwerfen" returns to it,
  // otherwise a main coverage switched on/off here would not match the table the customer lands on.
  const initialCoverageSelections = React.useRef(coverageSelections);
  const handleDiscard = () => {
    // An empty selection means the state was not hydrated yet when this page mounted: nothing to restore then.
    if (Object.values(initialCoverageSelections.current).some(Boolean)) setCoverageSelections(initialCoverageSelections.current);
    onBack();
  };

  const isYoungPet = isPetYoungerThanAgeBand(petBirthDate);
  const petLabel = petName?.trim() || t("common.yourPet");

  const selectedCoverageIds = React.useMemo(
    () => BASE_COVERAGES.filter((def) => coverageSelections[def.id]).map((def) => def.id),
    [coverageSelections]
  );

  // Set while a just-enabled coverage's own recommendation call is in flight — disables that
  // coverage's toggle and shows a spinner so the customer can't re-toggle mid-fetch.
  const [recommendationLoadingFor, setRecommendationLoadingFor] = React.useState<BaseCoverageId | null>(null);

  // "frei wählen" fields whose text field was left empty. For these two the empty entry has no state value of its own
  // (the other age band and the Jahresfranchise fall back to a default), so it is tracked here to block continuing.
  const [otherBandEmpty, setOtherBandEmpty] = React.useState(false);
  const [annualDeductibleEmpty, setAnnualDeductibleEmpty] = React.useState(false);
  const [preexistingOtherBandEmpty, setPreexistingOtherBandEmpty] = React.useState(false);
  const [preexistingSumEmpty, setPreexistingSumEmpty] = React.useState(false);
  const hasEmptyCustomEntry = otherBandEmpty || annualDeductibleEmpty || preexistingOtherBandEmpty || preexistingSumEmpty;

  /** Krankheit and Unfall share one API coverage (`diseaseAndAccident`), so a shared figure
   * (sum insured, deductible, sub-treatments) edited on one line is written to both lines'
   * state — otherwise un-checking Krankheit would silently fall back to Unfall's stale copy. */
  const withShared = <T,>(map: Partial<Record<BaseCoverageId, T>>, id: BaseCoverageId, value: T | undefined) => {
    const next = { ...map };
    for (const target of sharedLineIds(id)) next[target] = value;
    return next;
  };

  /** Writes the figures of a coverage that was just switched on. One rule for every case: for Krankheit and Unfall
   * (shared figures) what the customer already has — on this line or the other one — wins, and the recommendation
   * (`null` when none was fetched or available) only fills the gaps. The result goes to both lines. Other coverages
   * simply take the recommendation. Without one, sub-coverages default to on and the deductible falls back to
   * Krankheit's — a live figure of this customer rather than a hard-coded default. */
  const adoptFigures = (id: BaseCoverageId, recommended: CoverageState | null) => {
    const def = getBaseCoverage(id);
    const lines = sharedLineIds(id);
    const shared = lines.length > 1;
    const existing = <T,>(map: Partial<Record<BaseCoverageId, T>>): T | undefined =>
      shared ? lines.map((line) => map[line]).find((value) => value !== undefined) : undefined;
    const write = <T,>(map: Partial<Record<BaseCoverageId, T>>, set: (next: Partial<Record<BaseCoverageId, T>>) => void, value: T | undefined) => {
      if (value !== undefined) set(withShared(map, id, value));
    };
    const hasDeductible = def.deductible !== "none";

    write(coverageSumInsured, setCoverageSumInsured, existing(coverageSumInsured) ?? recommended?.coverageSumInsured[id]);
    write(coverageSumInsuredPerCase, setCoverageSumInsuredPerCase, existing(coverageSumInsuredPerCase) ?? recommended?.coverageSumInsuredPerCase[id]);
    if (hasDeductible) {
      write(coverageDeductibles, setCoverageDeductibles, existing(coverageDeductibles) ?? recommended?.coverageDeductibles[id] ?? coverageDeductibles.disease);
      if (def.deductible === "ageBanded") {
        write(
          coverageDeductiblesOtherBand,
          setCoverageDeductiblesOtherBand,
          existing(coverageDeductiblesOtherBand) ?? recommended?.coverageDeductiblesOtherBand?.[id] ?? coverageDeductiblesOtherBand.disease
        );
      }
    }

    const defaultSubs = Object.fromEntries(def.subs.map((sub) => [sub.id, true]));
    const subSelections = { ...(recommended ? (recommended.subCoverageSelections[id] ?? {}) : (subCoverageSelections[id] ?? defaultSubs)), ...(existing(subCoverageSelections) ?? {}) };
    const subSums = { ...(recommended?.subCoverageSumInsured[id] ?? {}), ...(existing(subCoverageSumInsured) ?? {}) };
    if (def.subs.length > 0 || Object.keys(subSelections).length > 0) {
      setSubCoverageSelections(withShared(subCoverageSelections, id, subSelections));
      setSubCoverageSumInsured(withShared(subCoverageSumInsured, id, subSums));
    }

    // Notfallbetreuung has no other figures than the sum per day, which the backend decides per species.
    if (id === "emergencyCare" && recommended?.emergencyCareSumInsuredPerDay) {
      setEmergencyCareSumInsuredPerDay(recommended.emergencyCareSumInsuredPerDay);
    }

    // The Jahresfranchise is one figure for both lines: only a first-time configuration takes the recommended one.
    const configuredBefore = lines.some((line) => coverageSumInsured[line] != null);
    if (shared && !configuredBefore && recommended?.annualDeductible != null) setAnnualDeductible(recommended.annualDeductible);
  };

  const toggleCoverage = async (id: BaseCoverageId, on: boolean) => {
    const nextSelections = { ...coverageSelections, [id]: on };
    for (const def of BASE_COVERAGES) {
      if (def.requires && !nextSelections[def.requires]) nextSelections[def.id] = false;
    }
    setCoverageSelections(nextSelections);

    if (!on) return;

    // Krankheit/Unfall: if a line of the pair is already fully configured (figures plus every sub-coverage this
    // line has), there is nothing to ask the backend — switching a line off and on again, or adding the second
    // line, keeps what the customer set up.
    const def = getBaseCoverage(id);
    const lines = sharedLineIds(id);
    const configured = (line: BaseCoverageId) =>
      coverageSumInsured[line] != null &&
      coverageDeductibles[line] != null &&
      def.subs.every((sub) => subCoverageSelections[line]?.[sub.id] !== undefined);
    if (lines.length > 1 && lines.some(configured)) {
      adoptFigures(id, null);
      return;
    }

    // Ask the backend to recommend fresh figures for just this newly-added coverage — every
    // other already-configured coverage is sent as-is, so their values aren't disturbed.
    setRecommendationLoadingFor(id);
    try {
      const recommended = await fetchCoverageRecommendation(id, {
        coverageSelections: nextSelections,
        subCoverageSelections,
        subCoverageSumInsured,
        coverageDeductibles,
        coverageDeductiblesOtherBand,
        coverageSumInsured,
        coverageSumInsuredPerCase,
        emergencyCareSumInsuredPerDay,
        annualDeductible,
      });
      adoptFigures(id, recommended);
    } finally {
      setRecommendationLoadingFor(null);
    }
  };

  /** Switches a sub-coverage on and, if it never had a sum insured (the backend recommended it off),
   * seeds a sensible one so the customer doesn't land on an empty required field. */
  const enableSub = (baseId: BaseCoverageId, subId: string) => {
    toggleSub(baseId, subId, true);
    const sub = getBaseCoverage(baseId).subs.find((s) => s.id === subId);
    if (sub && !sub.fixed && !getBaseCoverage(baseId).subsAreFlags && subCoverageSumInsured[baseId]?.[subId] == null) {
      setSubSumInsured(baseId, subId, DEFAULT_SUB_SUM_INSURED[subId]);
    }
  };

  const toggleSub = (baseId: BaseCoverageId, subId: string, on: boolean) => {
    setSubCoverageSelections(
      withShared(subCoverageSelections, baseId, { ...(subCoverageSelections[baseId] || {}), [subId]: on })
    );
  };

  const setSubSumInsured = (baseId: BaseCoverageId, subId: string, value: number | undefined) => {
    const nextForBase = { ...(subCoverageSumInsured[baseId] || {}) };
    if (value == null) {
      delete nextForBase[subId];
    } else {
      nextForBase[subId] = value;
    }
    setSubCoverageSumInsured(withShared(subCoverageSumInsured, baseId, nextForBase));
  };

  const getSubSumError = (baseId: BaseCoverageId, sub: SubCoverageDef): string | null => {
    const value = subCoverageSumInsured[baseId]?.[sub.id];
    const max = subSumInsuredMax(sub);
    if (value == null) return t("coverages.validation.subSumInsuredRequired");
    // Above the maximum of an unlimited-capable sub-coverage = unlimitiert (clamped in `SubSumInput`), never an error.
    const clamped = max >= UNLIMITED_SUM_INSURED && value > max ? max : value;
    if (clamped < SUB_SUM_INSURED_MIN || clamped > max) {
      return t("coverages.validation.subSumInsuredRange", { min: SUB_SUM_INSURED_MIN, max: max.toLocaleString("de-CH") });
    }
    // A sub-coverage can never pay out more than the main coverage's sum insured.
    const mainSum = sharedLineIds(baseId).includes("disease") ? coverageSumInsured[baseId] : undefined;
    if (mainSum != null && value > mainSum) {
      return t("coverages.validation.subSumExceedsMain", { max: mainSum.toLocaleString("de-CH") });
    }
    return null;
  };

  const hasSubSumErrors = React.useMemo(() => {
    for (const def of BASE_COVERAGES) {
      if (!coverageSelections[def.id] || def.subsAreFlags) continue;
      for (const sub of def.subs) {
        if (sub.fixed) continue;
        const included = !!subCoverageSelections[def.id]?.[sub.id];
        if (included && getSubSumError(def.id, sub)) return true;
      }
    }
    return false;
  }, [coverageSelections, subCoverageSelections, subCoverageSumInsured, coverageSumInsured]);

  const getDeductibleError = (id: BaseCoverageId): string | null => {
    const value = coverageDeductibles[id];
    if (value == null) return t("coverages.validation.deductibleRequired");
    if (value < DEDUCTIBLE_PERCENTAGE_MIN || value > DEDUCTIBLE_PERCENTAGE_MAX) {
      return t("coverages.validation.deductibleRange", { min: DEDUCTIBLE_PERCENTAGE_MIN, max: DEDUCTIBLE_PERCENTAGE_MAX });
    }
    return null;
  };

  const hasDeductibleErrors = React.useMemo(() => {
    for (const def of BASE_COVERAGES) {
      if (def.deductible === "none") continue;
      if (!coverageSelections[def.id]) continue;
      if (getDeductibleError(def.id)) return true;
    }
    return false;
  }, [coverageSelections, coverageDeductibles]);

  // Disease, accident, and preexistingConditions each have their own independent sumInsured
  // ceiling (see buildCoveragesPayload) — required whenever that coverage is selected.
  const getSumInsuredError = (id: BaseCoverageId): string | null => {
    const value = coverageSumInsured[id];
    if (value == null) return t("coverages.validation.sumInsuredRequired");
    if (id === "rescue") {
      return value < SUB_SUM_INSURED_MIN || value > baseSumInsuredMax(id)
        ? t("coverages.validation.subSumInsuredRange", { min: SUB_SUM_INSURED_MIN, max: baseSumInsuredMax(id).toLocaleString("de-CH") })
        : null;
    }
    if (id === "preexistingConditions") {
      return value < PREEXISTING_SUM_INSURED_MIN || value > PREEXISTING_SUM_INSURED_MAX
        ? t("coverages.validation.subSumInsuredRange", { min: PREEXISTING_SUM_INSURED_MIN, max: PREEXISTING_SUM_INSURED_MAX.toLocaleString("de-CH") })
        : null;
    }
    if (value < SUM_INSURED_MIN) {
      return t("coverages.validation.sumInsuredTooLow", { min: SUM_INSURED_MIN.toLocaleString("de-CH") });
    }
    return null;
  };

  const hasSumInsuredErrors = React.useMemo(() => {
    for (const id of ["disease", "accident", "preexistingConditions"] as const) {
      if (!coverageSelections[id]) continue;
      if (getSumInsuredError(id)) return true;
    }
    // Rettung & Bergung is requested unless switched off, so its sum has to be valid as well.
    if (coverageSelections.rescue !== false && getSumInsuredError("rescue")) return true;
    return false;
  }, [coverageSelections, coverageSumInsured]);

  // A coverage the underwriting rejected (and that is still switched on) can't be bought — the customer has to
  // switch it off first, otherwise the offer would be created with a coverage Simpego won't insure.
  const hasDeclinedCoverages = (Object.keys(declinedCoverages) as BaseCoverageId[]).some((id) =>
    isCoverageOn(coverageSelections, getBaseCoverage(id))
  );

  // Krankheit and/or Unfall is what the contract is built on: with both off nothing can be calculated or bought.
  const noMainCoverage = !coverageSelections.disease && !coverageSelections.accident;

  const handleSubmit = () => {
    if (!hasSelectedPlan || currentApiPrice === null) {
      showErrorDialog(t("priceCustomization.validation.selectProductVariant"));
      return;
    }
    if (noMainCoverage || hasEmptyCustomEntry || hasSumInsuredErrors || hasSubSumErrors || hasDeductibleErrors || hasDeclinedCoverages) {
      return;
    }
    // `startOfInsuranceError` can already be flagged (e.g. the customer cleared the field and
    // blurred it) without `startOfInsurance` itself changing — Flatpickr only calls back with a
    // fully-parsed date, so the state can still hold the last valid date while the input looks
    // empty. Keep that flag rather than silently overwriting it with a fresh validation of the
    // (stale-but-valid) state value, which would let the customer through regardless.
    const dateError = startOfInsuranceError || validateStartOfInsuranceDate(startOfInsurance, earliestStartOfInsurance, t);
    setStartOfInsuranceError(dateError);
    if (dateError) {
      return;
    }
    onCustomizeProduct(
      selectedProduct,
      currentApiPrice,
      coverageDeductibles,
      subCoverageSelections,
      coverageSumInsured,
      dailyTermination,
      paymentInterval,
      paymentMethod,
      startOfInsurance
    );
  };

  const paymentIntervalOptions = [1, 2, 4, 12];
  const paymentMethodOptions = ["credit_card", "ESR"];

  /** Name of a sub-coverage row (spans only, so it can sit inside a button). */
  const renderSubName = (sub: SubCoverageDef, included: boolean, declinedSub: boolean, stateId: BaseCoverageId) => (
    <span className={cn("block text-sm", !included && "text-muted-foreground")}>
      {t(sub.labelKey)}
      {declinedSub && (
        <span className="ml-2"><UwrDeclinedBadge label={t("coverages.subDeclinedByUwr")} reason={coverageUnderwritingReasons[stateId]} /></span>
      )}
    </span>
  );

  /** Info icon that opens a dialog (like the other info icons) with the explanation of the Baustein. */
  const renderSubInfo = (sub: SubCoverageDef) => (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0">
          <Info className="h-5! w-5! text-muted-foreground" />
          <span className="sr-only">{`${t("common.info")}: ${t(sub.labelKey)}`}</span>
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t(sub.infoTitleKey ?? sub.labelKey)}</DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-2">
              {sub.infoKey && <p className="whitespace-pre-line">{t(sub.infoKey)}</p>}
            </div>
          </DialogDescription>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );

  /** Sub-coverage rows (switch, name, Wartefrist, sum insured) shared by the merged Krankheit-und-Unfall
   * card and the Vorerkrankung card. `flagsOnly` hides the sum fields (Vorerkrankung's subs are plain flags). */
  const renderSubList = (stateId: BaseCoverageId, subs: SubCoverageDef[], flagsOnly: boolean, extraRows?: React.ReactNode) => (
    <div className="space-y-1 border-t border-border pt-5">
      <div className="flex items-center gap-2">
        <p className="text-sm font-semibold">{t("coverages.subCoveragesLabel")}</p>
        <InfoDialog title={t("coverages.bausteine.infoTitle")}>{t("coverages.bausteine.info")}</InfoDialog>
      </div>
      {coverageUnderwritingReasons[stateId] && <p className="text-xs text-destructive">{coverageUnderwritingReasons[stateId]}</p>}
      <div className="divide-y divide-border">
        {subs.map((sub) => {
          const included = !!subCoverageSelections[stateId]?.[sub.id];
          const sum = subCoverageSumInsured[stateId]?.[sub.id];
          const declinedSub = !!declinedSubs[stateId]?.[sub.id];
          return (
            <div key={sub.id} className="flex flex-wrap items-center gap-x-3 gap-y-2 py-3">
              <div className="flex min-w-0 flex-1 items-center gap-2">
                {sub.fixed || stateId === "preexistingConditions" ? (
                  <LockedCheck checked={included} label={t(sub.labelKey)}>
                    <span className="min-w-0">{renderSubName(sub, included, declinedSub, stateId)}</span>
                  </LockedCheck>
                ) : (
                  <CoverageCheck
                    checked={included}
                    label={t(sub.labelKey)}
                    disabled={isLoadingPrice}
                    onChange={(v) => (v ? enableSub(stateId, sub.id) : toggleSub(stateId, sub.id, false))}
                  >
                    <span className="min-w-0">{renderSubName(sub, included, declinedSub, stateId)}</span>
                  </CoverageCheck>
                )}
                {renderSubInfo(sub)}
              </div>
              {included && sub.fixed && !flagsOnly && sum != null && <span className="text-xs text-muted-foreground">{formatSumInsured(sum, t)}</span>}
              {included && !sub.fixed && !flagsOnly && (
                <SubSumInput
                  value={sum}
                  onChange={(v) => setSubSumInsured(stateId, sub.id, v)}
                  onZero={() => toggleSub(stateId, sub.id, false)}
                  max={subSumInsuredMax(sub)}
                  fallback={DEFAULT_SUB_SUM_INSURED[sub.id]}
                  disabled={isLoadingPrice}
                  error={getSubSumError(stateId, sub) ?? undefined}
                />
              )}
            </div>
          );
        })}
        {extraRows}
      </div>
    </div>
  );

  /** Krankheit und Unfall are one API coverage (`diseaseAndAccident`), so they get one card: line
   * chips on top, then the figures both lines share (sum insured, cost sharing incl. Jahresfranchise,
   * sub-coverages). Shared state lives under the primary line and is written to both lines. */
  const renderDiseaseAndAccident = () => {
    const def = getBaseCoverage("disease");
    const primary: BaseCoverageId = primaryLineId(coverageSelections) ?? "disease";
    const diseaseOn = !!coverageSelections.disease;
    const accidentOn = !!coverageSelections.accident;
    const anyOn = diseaseOn || accidentOn;
    const loading = recommendationLoadingFor === "disease" || recommendationLoadingFor === "accident";
    const declined = declinedCoverages[primary];
    const pct = coverageDeductibles[primary];
    const subs = def.subs.filter((sub) => !sub.diseaseOnly || diseaseOn);
    const title = diseaseOn && !accidentOn ? t("coverages.disease.label") : accidentOn && !diseaseOn ? t("coverages.accident.label") : t("coverages.diseaseAndAccident.label");

    return (
      <CoverageSection
        key="diseaseAndAccident"
        title={
          <>
            <span className="text-base font-semibold">{title}</span>
          </>
        }
        aside={
          loading ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          ) : declined !== undefined ? (
            <UwrDeclinedBadge label={t("coverages.coverageDeclinedByUwr")} />
          ) : (
            anyOn && coveragePremiums[primary] !== undefined && (
              <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">
                {t("coverages.partialPremiumLabel")}: CHF {coveragePremiums[primary]!.toFixed(2)}
              </span>
            )
          )
        }
      >
        <div className="space-y-1">
          <Label className="text-sm font-medium">{t("coverages.diseaseAndAccident.linesLabel")}</Label>
          <div className="flex flex-wrap gap-x-6 gap-y-2 pt-1">
            {([["disease", diseaseOn], ["accident", accidentOn]] as const).map(([id, isOn]) => (
              <div key={id} className="flex items-center gap-2">
                <CoverageCheck
                  checked={isOn}
                  label={t(getBaseCoverage(id).labelKey)}
                  disabled={loading}
                  onChange={(value) => toggleCoverage(id, value)}
                >
                  <span className={cn("text-base font-semibold", isOn ? "text-primary" : "text-foreground")}>{t(getBaseCoverage(id).labelKey)}</span>
                </CoverageCheck>
                <InfoDialog title={t(getBaseCoverage(id).infoTitleKey)}>{t(getBaseCoverage(id).infoDescriptionKey)}</InfoDialog>
              </div>
            ))}
          </div>
          {!anyOn && <p className="text-xs text-destructive">{t("coverages.mainCoverageRequired")}</p>}
          {declined && <p className="text-xs text-destructive">{declined}</p>}
        </div>

        {anyOn && (
          <div className="space-y-5">
            <div className="space-y-2">
              <SumInsuredInput
                value={coverageSumInsured[primary]}
                onChange={(value) => setCoverageSumInsured(withShared(coverageSumInsured, primary, value))}
                max={baseSumInsuredMax(primary)}
                disabled={isLoadingPrice}
                error={getSumInsuredError(primary) ?? undefined}
              />
            </div>

            <div className="space-y-4 border-t border-border pt-5">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold">{t("coverages.costSharing.title")}</p>
                  <InfoDialog title={t("coverages.costShare.infoTitle")}>{t("coverages.costShare.info")}</InfoDialog>
                </div>
                <p className="text-sm text-muted-foreground">{t("coverages.costShare.question")}</p>
              </div>
              {isYoungPet ? (
                <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
                  <CostShareField
                    id="deductible-up-to-9"
                    label={t("coverages.costShare.until9Birthday", { petName: petLabel })}
                    value={pct}
                    onChange={(value) => setCoverageDeductibles(withShared(coverageDeductibles, primary, value))}
                    disabled={isLoadingPrice}
                  />
                  <CostShareField
                    id="deductible-from-9"
                    label={t("coverages.costShare.from9Birthday", { petName: petLabel })}
                    value={otherBandEmpty ? undefined : (coverageDeductiblesOtherBand[primary] ?? pct)}
                    onChange={(value) => {
                      setOtherBandEmpty(value == null);
                      if (value != null) setCoverageDeductiblesOtherBand(withShared(coverageDeductiblesOtherBand, primary, value));
                    }}
                    onEmptyChange={(empty) => !empty && setOtherBandEmpty(false)}
                    disabled={isLoadingPrice}
                  />
                </div>
              ) : (
                <CostShareField
                  id="deductible-from-9"
                  label={t("coverages.costShare.from9Birthday", { petName: petLabel })}
                  value={pct}
                  onChange={(value) => setCoverageDeductibles(withShared(coverageDeductibles, primary, value))}
                  disabled={isLoadingPrice}
                />
              )}
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Label htmlFor="annual-deductible" className="text-sm font-medium">{t("coverages.annualDeductibleLabel")}</Label>
                  <InfoDialog title={t("coverages.annualDeductibleInfoTitle")} srLabel={t("coverages.annualDeductibleLabel")}>{t("coverages.annualDeductibleInfo")}</InfoDialog>
                </div>
                <PresetValueField
                  id="annual-deductible"
                  value={annualDeductibleEmpty ? undefined : (annualDeductible ?? 0)}
                  onChange={(value) => {
                    setAnnualDeductibleEmpty(value == null);
                    if (value != null) setAnnualDeductible(value);
                  }}
                  onEmptyChange={(empty) => !empty && setAnnualDeductibleEmpty(false)}
                  presets={ANNUAL_DEDUCTIBLE_PRESETS}
                  formatValue={(preset) => `CHF ${preset.toLocaleString("de-CH")}`}
                  min={0}
                  max={ANNUAL_DEDUCTIBLE_MAX}
                  suffix="CHF"
                  disabled={isLoadingPrice}
                  requiredMessage={t("coverages.amountRequired")}
                  hint={t("coverages.annualDeductibleHint", { max: ANNUAL_DEDUCTIBLE_MAX.toLocaleString("de-CH") })}
                />
              </div>
            </div>

            {renderSubList(
              primary,
              subs,
              false,
              // Volldeckung bei Erbkrankheiten: the API's waiverHereditaryLimits flag.
              diseaseOn && (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2 py-3">
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                    <CoverageCheck
                      checked={isFullCoverRequested(subCoverageSelections, primary)}
                      label={t("coverages.subs.waiverHereditaryLimits.label")}
                      disabled={isLoadingPrice}
                      onChange={(full) => toggleSub(primary, WAIVER_HEREDITARY_LIMITS_KEY, full)}
                    >
                      <span className="min-w-0 text-sm">{t("coverages.subs.waiverHereditaryLimits.label")}</span>
                    </CoverageCheck>
                    <InfoDialog title={t("coverages.subs.waiverHereditaryLimits.infoTitle")} srLabel={t("coverages.subs.waiverHereditaryLimits.label")}>{t("coverages.subs.waiverHereditaryLimits.info")}</InfoDialog>
                  </div>
                </div>
              )
            )}
          </div>
        )}
      </CoverageSection>
    );
  };

  /** Vorerkrankung's sum insured and cost share, laid out like the Krankheit-und-Unfall card (no Jahresfranchise: that
   * one is shared and edited there). */
  const renderPreexistingFigures = () => {
    const id: BaseCoverageId = "preexistingConditions";
    const pct = coverageDeductibles[id];
    return (
      <div className="space-y-5">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Label htmlFor="preexisting-sum-insured" className="text-sm font-medium">{t("coverages.sumInsuredLabel")}</Label>
            <InfoDialog title={t("coverages.sumInsuredInfoTitle")}>{t("coverages.sumInsuredInfoDescription")}</InfoDialog>
          </div>
          <PresetValueField
            id="preexisting-sum-insured"
            value={coverageSumInsured[id]}
            onChange={(value) => setCoverageSumInsured({ ...coverageSumInsured, [id]: value })}
            onEmptyChange={setPreexistingSumEmpty}
            onZero={() => toggleCoverage(id, false)}
            presets={PREEXISTING_SUM_INSURED_PRESETS}
            formatValue={(preset) => `CHF ${preset.toLocaleString("de-CH")}`}
            min={PREEXISTING_SUM_INSURED_MIN}
            max={PREEXISTING_SUM_INSURED_MAX}
            suffix="CHF"
            disabled={isLoadingPrice}
            error={getSumInsuredError(id) ?? undefined}
            requiredMessage={t("coverages.validation.sumInsuredRequired")}
            hint={t("coverages.preexistingSumInsuredHint", {
              min: PREEXISTING_SUM_INSURED_MIN,
              max: PREEXISTING_SUM_INSURED_MAX.toLocaleString("de-CH"),
            })}
          />
        </div>

        <div className="space-y-4 border-t border-border pt-5">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <p className="text-sm font-semibold">{t("coverages.costSharing.title")}</p>
              <InfoDialog title={t("coverages.costShare.infoTitle")}>{t("coverages.costShare.info")}</InfoDialog>
            </div>
            <p className="text-sm text-muted-foreground">{t("coverages.costShare.question")}</p>
          </div>
          {isYoungPet ? (
            <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
              <CostShareField
                id="preexisting-deductible-up-to-9"
                label={t("coverages.costShare.until9Birthday", { petName: petLabel })}
                value={pct}
                onChange={(value) => setCoverageDeductibles({ ...coverageDeductibles, [id]: value })}
                disabled={isLoadingPrice}
              />
              <CostShareField
                id="preexisting-deductible-from-9"
                label={t("coverages.costShare.from9Birthday", { petName: petLabel })}
                value={preexistingOtherBandEmpty ? undefined : (coverageDeductiblesOtherBand[id] ?? pct)}
                onChange={(value) => {
                  setPreexistingOtherBandEmpty(value == null);
                  if (value != null) setCoverageDeductiblesOtherBand({ ...coverageDeductiblesOtherBand, [id]: value });
                }}
                onEmptyChange={(empty) => !empty && setPreexistingOtherBandEmpty(false)}
                disabled={isLoadingPrice}
              />
            </div>
          ) : (
            <CostShareField
              id="preexisting-deductible-from-9"
              label={t("coverages.costShare.from9Birthday", { petName: petLabel })}
              value={pct}
              onChange={(value) => setCoverageDeductibles({ ...coverageDeductibles, [id]: value })}
              disabled={isLoadingPrice}
            />
          )}
        </div>
      </div>
    );
  };

  /** "Zusatzdeckungen": Bergung & Rettung can be switched off, Schäden an Mietobjekten and
   * Notfallbetreuung are always part of the contract and shown as locked checked boxes. */
  const renderFurtherBenefits = () => {
    const rescue = getBaseCoverage("rescue");
    const rescueOn = isCoverageOn(coverageSelections, rescue);
    const rescueLoading = recommendationLoadingFor === "rescue";
    const emergencyCareOn = isCoverageOn(coverageSelections, getBaseCoverage("emergencyCare"));
    const emergencyCareLoading = recommendationLoadingFor === "emergencyCare";
    const rescueError = getSumInsuredError("rescue");
    const chf = (value: number) => `CHF ${value.toLocaleString("de-CH")}`;
    const detailClass = "text-sm text-muted-foreground";

    return (
      <CoverageSection key="furtherBenefits" title={<span className="text-base font-semibold">{t("coverages.furtherBenefits")}</span>}>
        <div className="-my-3 divide-y divide-border">
          <div className="space-y-3 py-3">
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
              <div className="flex min-w-0 items-center gap-2">
                <CoverageCheck
                  checked={rescueOn}
                  label={t(rescue.labelKey)}
                  disabled={rescueLoading}
                  onChange={(v) => toggleCoverage("rescue", v)}
                >
                  <span className="text-sm">{t(rescue.labelKey)}</span>
                </CoverageCheck>
                <InfoDialog title={t(rescue.infoTitleKey)}>{t(rescue.infoDescriptionKey)}</InfoDialog>
              </div>
              {rescueLoading ? (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              ) : rescueOn && declinedCoverages.rescue !== undefined ? (
                <UwrDeclinedBadge label={t("coverages.coverageDeclinedByUwr")} reason={declinedCoverages.rescue} />
              ) : (
                rescueOn && coveragePremiums.rescue !== undefined && (
                  <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">
                    {t("coverages.partialPremiumLabel")}: CHF {coveragePremiums.rescue.toFixed(2)}
                  </span>
                )
              )}
            </div>
            {rescueOn && declinedCoverages.rescue && <p className="text-xs text-destructive">{declinedCoverages.rescue}</p>}
            {rescueOn && (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Label htmlFor="rescue-sum-insured" className="text-sm font-medium sm:whitespace-nowrap">{t("coverages.rescueSumInsuredLabel")}</Label>
                  <InfoDialog title={t("coverages.sumInsuredInfoTitle")}>{t("coverages.sumInsuredInfoDescription")}</InfoDialog>
                </div>
                <div className="max-w-xs space-y-1">
                  <NumericInput
                    id="rescue-sum-insured"
                    value={coverageSumInsured.rescue}
                    onChange={(value) => setCoverageSumInsured({ ...coverageSumInsured, rescue: value })}
                    onZero={() => toggleCoverage("rescue", false)}
                    min={1}
                    max={10000}
                    suffix="CHF"
                    disabled={isLoadingPrice}
                    error={!!rescueError}
                    className="w-full"
                  />
                  <p className={cn("text-xs", rescueError ? "text-destructive" : "text-muted-foreground")}>
                    {rescueError ?? t("coverages.rescueSumInsuredHint", { max: (10000).toLocaleString("de-CH") })}
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-3 py-3">
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
              <div className="flex min-w-0 items-center gap-2">
                <CoverageCheck
                  checked={emergencyCareOn}
                  label={t("coverages.emergencyCare.label")}
                  disabled={emergencyCareLoading}
                  onChange={(v) => toggleCoverage("emergencyCare", v)}
                >
                  <span className="text-sm">{t("coverages.emergencyCare.label")}</span>
                </CoverageCheck>
                <InfoDialog title={t("coverages.emergencyCare.infoTitle")}>{t("coverages.emergencyCare.infoDescription")}</InfoDialog>
              </div>
              {emergencyCareLoading ? (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              ) : emergencyCareOn && declinedCoverages.emergencyCare !== undefined ? (
                <UwrDeclinedBadge label={t("coverages.coverageDeclinedByUwr")} reason={declinedCoverages.emergencyCare} />
              ) : (
                emergencyCareOn && coveragePremiums.emergencyCare !== undefined && (
                  <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">
                    {t("coverages.partialPremiumLabel")}: CHF {coveragePremiums.emergencyCare.toFixed(2)}
                  </span>
                )
              )}
            </div>
            {emergencyCareOn && declinedCoverages.emergencyCare && (
              <p className="text-xs text-destructive">{declinedCoverages.emergencyCare}</p>
            )}
            {emergencyCareOn && (
              <p className={detailClass}>
                {t("priceCustomization.table.emergencyCareDetail", { amount: chf(Number(emergencyCareSumInsuredPerDay ?? getEmergencyCareDefaultSumPerDay(petType))), days: 20 })}
              </p>
            )}
          </div>
        </div>
      </CoverageSection>
    );
  };

  const isButtonDisabled = isLoadingPrice || !hasSelectedPlan || currentApiPrice === null || underwritingFailed || noMainCoverage || hasEmptyCustomEntry || hasSubSumErrors || hasDeductibleErrors || hasSumInsuredErrors || hasDeclinedCoverages || !!startOfInsuranceError || recommendationLoadingFor !== null;
  const isSecondaryButtonDisabled = isLoadingPrice;

  return (
    <Card
      className="w-full max-w-4xl mx-auto border-none shadow-none"
      style={{ "--cart-offset": `${cartOffset}px`, "--band": BAND_COLOR } as React.CSSProperties}
    >
      <CardHeader>
        <CardTitle className="text-3xl font-bold">
          {t("banner-header-2", { petName: petName?.trim() || t("common.yourPet") })}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-8">

        {/* Per-coverage sections */}
        <div className="space-y-8">
          {BASE_COVERAGES.filter((def) => !def.hiddenInCustomization && def.id !== "rescue").map((def) => {
            if (def.id === "accident") return null; // rendered together with Krankheit
            if (def.id === "disease") return renderDiseaseAndAccident();
            const on = isCoverageOn(coverageSelections, def);
            const gated = !!def.requires && !coverageSelections[def.requires];
            const isRecommendationLoading = recommendationLoadingFor === def.id;

            return (
              <CoverageSection
                key={def.id}
                title={
                  <>
                    <div className="flex items-center gap-2">
                      <CoverageCheck
                        checked={on}
                        label={t(def.labelKey)}
                        disabled={gated || isRecommendationLoading}
                        onChange={(v) => toggleCoverage(def.id, v)}
                      >
                        <span className="text-base font-semibold">{t(def.labelKey)}</span>
                      </CoverageCheck>
                      <InfoDialog title={t(def.infoTitleKey)}>{t(def.infoDescriptionKey)}</InfoDialog>
                    </div>
                    {/* Vorerkrankung is not configurable: the header just states what is covered and the fixed figures. */}
                    {on && def.id === "preexistingConditions" && (
                      <>
                        <p className={`mt-0.5 ${CHECK_INDENT} text-sm font-medium text-muted-foreground`}>{def.subs.map((sub) => t(sub.labelKey)).join(", ")}</p>
                        {coverageSumInsured[def.id] != null && (
                          <p className={`${CHECK_INDENT} text-sm font-medium text-muted-foreground`}>
                            {t("coverages.sumInsuredLabel")} {formatSumInsured(coverageSumInsured[def.id]!, t)}
                            {coverageDeductibles[def.id] != null && ` – ${t("coverages.costShare.label")} ${100 - coverageDeductibles[def.id]!}%`}
                          </p>
                        )}
                      </>
                    )}
                  </>
                }
                aside={
                  <div className="flex items-center gap-3">
                    {isRecommendationLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    ) : on && declinedCoverages[def.id] !== undefined ? (
                      <UwrDeclinedBadge label={t("coverages.coverageDeclinedByUwr")} />
                    ) : (
                      on && coveragePremiums[def.id] !== undefined && (
                        <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">
                          {t("coverages.partialPremiumLabel")}: CHF {coveragePremiums[def.id]!.toFixed(2)}
                        </span>
                      )
                    )}
                  </div>
                }
              >
                {(gated || (on && declinedCoverages[def.id])) && (
                  <>
                    {on && declinedCoverages[def.id] && (
                      <p className="text-xs text-destructive">{declinedCoverages[def.id]}</p>
                    )}
                  </>
                )}
                {on && def.id === "preexistingConditions" && renderPreexistingFigures()}
              </CoverageSection>
            );
          })}
          {renderFurtherBenefits()}
          {selectedCoverageIds.length === 0 && (
            <p className="text-sm text-muted-foreground">{t("coverages.noneSelectedHint")}</p>
          )}
        </div>

        {/* Zahlung */}
        <div id="payment-options" className="space-y-5 border-t pt-6 scroll-mt-(--cart-offset)">
          <h3 className="text-2xl font-bold">{t("priceCustomization.paymentOptions")}</h3>

          <StartOfInsuranceInput
            value={startOfInsurance}
            onChange={setStartOfInsurance}
            min={earliestStartOfInsurance}
            error={startOfInsuranceError}
            setError={setStartOfInsuranceError}
            isLoading={isLoadingPrice}
            labelText={t("priceCustomization.startOfInsurance")}
            hintText={
              earliestStartOfInsurance > getEarliestStartOfInsurance()
                ? t("coverages.startOfInsuranceYoungPetHint", { date: earliestStartOfInsurance.toLocaleDateString("de-CH") })
                : earliestStartOfInsurance > getTomorrowAtMidnight()
                  ? t("priceCustomization.startOfInsuranceMinimumDateHint", { date: earliestStartOfInsurance.toLocaleDateString("de-CH") })
                  : t("priceCustomization.startOfInsuranceHint")
            }
            infoTitle={t("start-of-insurance-info-header")}
            infoDescription={t("start-of-insurance-info-content")}
          />

          <div className="space-y-2 field-row">
            <div className="flex items-center gap-2">
              <Label htmlFor="daily-termination" className="text-sm font-medium">{t("coverages.dailyTerminationLabel")}</Label>
              <InfoDialog title={t("coverages.dailyTerminationLabel")}>{t("coverages.dailyTerminationInfo")}</InfoDialog>
            </div>
            <Select
              value={dailyTermination ? "yes" : "no"}
              onValueChange={(value) => setDailyTermination(value === "yes")}
              disabled={isLoadingPrice}
            >
              <SelectTrigger id="daily-termination" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="yes">{t("common.yes")}</SelectItem>
                <SelectItem value="no">{t("common.no")}</SelectItem>
              </SelectContent>
            </Select>
            {dailyTermination && dailyTerminationPremium != null && (
              <p className="text-xs text-muted-foreground">
                {t("coverages.partialPremiumLabel")}: CHF {dailyTerminationPremium.toFixed(2)}
              </p>
            )}
          </div>

          {isCustomAgentId && (
            <DeductibleResetDateInput
              firstDue={firstDue}
              startOfInsurance={startOfInsurance}
              onChange={setFirstDue}
              disabled={isLoadingPrice}
              hasDeductible={(annualDeductible ?? 0) > 0}
            />
          )}

          <div className="space-y-2 field-row">
            <Label htmlFor="payment-interval" className="text-sm font-medium">
              {t("closing-payment-frequency-header")}
            </Label>
            <Select
              value={String(paymentInterval)}
              onValueChange={(value) => setPaymentInterval(Number(value) as 1 | 2 | 4 | 12)}
              disabled={isLoadingPrice}
            >
              <SelectTrigger id="payment-interval" className="w-full">
                <SelectValue placeholder={t("priceCustomization.selectPaymentInterval")} />
              </SelectTrigger>
              <SelectContent>
                {paymentIntervalOptions.map((interval) => (
                  <SelectItem key={interval} value={String(interval)}>
                    {t(`priceCustomization.paymentIntervalOptionsWithDiscount.${interval}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2 field-row">
            <div className="flex items-center gap-2">
              <Label htmlFor="payment-method" className="text-sm font-medium">
                {t("priceCustomization.paymentMethod")}
              </Label>
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0">
                    <Info className="h-4 w-4 text-muted-foreground" />
                    <span className="sr-only">{`${t("common.info")}: ${t("priceCustomization.paymentMethod")}`}</span>
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{t("priceCustomization.paymentMethodInfoTitle")}</DialogTitle>
                    <DialogDescription asChild>
                      <div dangerouslySetInnerHTML={{ __html: t("priceCustomization.paymentMethodInfoDescription") || "" }} />
                    </DialogDescription>
                  </DialogHeader>
                </DialogContent>
              </Dialog>
            </div>
            <Select
              value={paymentMethod}
              onValueChange={(value) => setPaymentMethod(value as "credit_card" | "ESR")}
              disabled={isLoadingPrice}
            >
              <SelectTrigger id="payment-method" className="w-full">
                <SelectValue placeholder={t("priceCustomization.selectPaymentMethod")} />
              </SelectTrigger>
              <SelectContent>
                {paymentMethodOptions.map((method) => (
                  <SelectItem key={method} value={method} disabled={paymentInterval === 12 && method === "ESR"}>
                    {t(`priceCustomization.paymentMethodOptions.${method}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardContent>
      {!noMainCoverage && hasDeclinedCoverages && (
        <p className="px-6 pb-3 text-sm text-destructive">
          {t("coverages.declinedBlocksContinue")}
        </p>
      )}
      <CardFooter className="flex flex-col-reverse gap-3 md:flex-row md:gap-4">
        <Button variant="outline" onClick={handleDiscard} className="w-full h-12 text-lg font-bold" disabled={isSecondaryButtonDisabled}>
          {t("priceCustomization.discard")}
        </Button>
        <Button onClick={handleSubmit} className="w-full h-12 text-lg font-bold bg-continue-button text-continue-button-foreground hover:bg-continue-button/90" disabled={isButtonDisabled}>
          {isLoadingPrice && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {t("priceCustomization.continue")}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default ProductCustomizationOptions;
